import { gql } from '@apollo/client';
import { Box, Button, Stack, Text, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { PASSWORD_CONNECTION } from '~components/organization/SsoTable/types';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { useAuthentication } from './../../auth/useAuthentication';
import { useRequestPasswordResetEmailMutation } from './__generated__/PrivacyAndSecuritySettings.graphql';

export function PrivacyAndSecuritySettings() {
  const { me } = useAuth();
  const toast = useToast();
  const { user } = useAuthentication();
  const storageKey = `pwdResetTimer_${user.email}`;
  const [timer, setTimer] = useState(0);
  const isDisabled = timer > 0;
  const [requestPasswordResetEmail, { loading }] = useRequestPasswordResetEmailMutation();

  useEffect(() => {
    const storedTimestamp = localStorage.getItem(storageKey);
    if (storedTimestamp) {
      const elapsedTime = Math.floor((Date.now() - Number(storedTimestamp)) / 1000);
      const remainingTime = Math.max(180 - elapsedTime, 0);
      setTimer(remainingTime);
      if (remainingTime === 0) {
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey]);

  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(() => {
        setTimer((prev) => {
          const newTime = prev - 1;
          if (newTime === 0) {
            localStorage.removeItem(storageKey);
          }
          return newTime;
        });
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [storageKey, timer]);

  const handleRequestPasswordChange = async () => {
    try {
      setTimer(180);
      localStorage.setItem(storageKey, Date.now().toString());
      const { data } = await requestPasswordResetEmail();
      if (isNil(data)) throw new Error('no data received');
      if (data.selfChangePwd.status == false) throw new Error('could not send reset email');
      toast({
        status: 'success',
        title: t('passwordReset'),
        description: t('resetMailSent'),
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('passwordReset'),
        description: t('resetMailSentErr'),
      });
    }
  };

  const isPreferredConnectionSet = !!me?.preferredConnection;
  const isPreferredConnectionPassword = me?.preferredConnection === PASSWORD_CONNECTION;
  const showChangePasswordOption = !isPreferredConnectionSet || isPreferredConnectionPassword;

  return (
    <VerticalTabContent title={t('privacySecurity')}>
      <Stack direction="row" spacing="20" mb="6">
        <Box flex="1">
          <Text fontSize="md" color="gray.500" maxWidth="700">
            {showChangePasswordOption ? (
              <Trans i18nKey="changePasswordMessage" components={{ bold: <b /> }} />
            ) : (
              t('ssoEnabledMessage')
            )}
          </Text>
        </Box>
        {showChangePasswordOption && (
          <Box>
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={handleRequestPasswordChange}
              isDisabled={loading || isDisabled}
            >
              {t('changePassword')}
            </Button>
            {isDisabled && (
              <Text mt={2} color="gray.500">
                {t('timerMsg', { time: timer })}
              </Text>
            )}
          </Box>
        )}
      </Stack>
    </VerticalTabContent>
  );
}

PrivacyAndSecuritySettings.graphql = {
  mutations: {
    requestChangePassword: gql`
      mutation requestPasswordResetEmail {
        selfChangePwd {
          status
        }
      }
    `,
  },
};
